import { format as formatDateFns, isFuture, parseISO } from 'date-fns';

import { UK_COUNTRY } from '@lib/utils/constants';

import { UNION_STATUS_TYPE } from './constants/applicationConstants';

export const getApplicationUuidFromUrl = (url: string) => {
  if (!url) return;

  const searchString = 'apps/';
  const index = url.indexOf(searchString);
  const applicationId = url.substring(index + searchString.length);

  return applicationId.split(/\?|\//gi)[0];
};

export const getApplicationDeadlineInfo = (
  closeSubmissionsDateTime: string | null,
  applicationStatus: JobsBoardApplicationStatus | null,
  projectCountry: string | undefined
) => {
  const hasDeadline =
    !!closeSubmissionsDateTime &&
    isFuture(parseISO(closeSubmissionsDateTime)) &&
    applicationStatus === 'open';

  const finalDateFormat =
    projectCountry === UK_COUNTRY
      ? "d MMM, yyyy 'at' h:mm a"
      : "MMM d, yyyy 'at' h:mm a";

  const formattedDeadline = hasDeadline
    ? formatDateFns(parseISO(closeSubmissionsDateTime), finalDateFormat)
    : null;

  return { hasDeadline, formattedDeadline };
};

export type JobPostInput = {
  description?: JobsBoardApplication['description'];
  rate?: Pick<
    JobsBoardApplication['rate'],
    'fitting_rate_note' | 'interview_rate_note'
  >;
  union_rate?: Pick<
    JobsBoardApplication['union_rate'],
    'fitting_rate_note' | 'interview_rate_note'
  >;
  project_units?: JobsBoardApplication['project_units'];
  union_status?: JobsBoardApplication['union_status'];
};

const EMPTY_RATE_NOTES_DISPLAY = {
  hasRateNotes: false,
  unionFittingRateNote: null,
  unionInterviewRateNote: null,
  nonUnionFittingRateNote: null,
  nonUnionInterviewRateNote: null,
} as const;

const isRateNoteUnitsEnabled = () =>
  process.env.NEXT_PUBLIC_ENABLE_RATE_NOTE_UNITS === 'true';

export const handleJobPostDescriptionDisplay = (
  jobPost: JobPostInput | null | undefined
) => {
  const trimmedDescription = jobPost?.description?.trim();
  const hasDescription = !!trimmedDescription;

  if (!isRateNoteUnitsEnabled()) {
    return {
      ...EMPTY_RATE_NOTES_DISPLAY,
      hasDescription,
    };
  }

  const unitNameToUnit =
    jobPost?.project_units?.reduce((acc, unit) => {
      acc[unit.name] = unit;

      return acc;
    }, {} as { [unitName: string]: ProjectUnit }) ?? {};

  const hasProjectUnitInterview = Number.isInteger(
    unitNameToUnit?.['Interview']?.id
  );

  const hasProjectUnitFitting = Number.isInteger(
    unitNameToUnit?.['Fitting']?.id
  );

  const isApplicationUnionType = (
    [
      UNION_STATUS_TYPE.SAG_AFTRA,
      UNION_STATUS_TYPE.SAG_AFTRA_NON_UNION,
    ] as string[]
  ).includes(jobPost?.union_status);

  const isApplicationNonUnionType = (
    [
      UNION_STATUS_TYPE.NON_UNION,
      UNION_STATUS_TYPE.SAG_AFTRA_NON_UNION,
    ] as string[]
  ).includes(jobPost?.union_status);

  const unionFittingRateNote =
    isApplicationUnionType &&
    hasProjectUnitFitting &&
    jobPost?.union_rate?.fitting_rate_note?.trim()
      ? jobPost?.union_rate?.fitting_rate_note
      : null;

  const unionInterviewRateNote =
    isApplicationUnionType &&
    hasProjectUnitInterview &&
    jobPost?.union_rate?.interview_rate_note?.trim()
      ? jobPost?.union_rate?.interview_rate_note
      : null;

  const nonUnionFittingRateNote =
    isApplicationNonUnionType &&
    hasProjectUnitFitting &&
    jobPost?.rate?.fitting_rate_note?.trim()
      ? jobPost?.rate?.fitting_rate_note
      : null;

  const nonUnionInterviewRateNote =
    isApplicationNonUnionType &&
    hasProjectUnitInterview &&
    jobPost?.rate?.interview_rate_note?.trim()
      ? jobPost?.rate?.interview_rate_note
      : null;

  const hasRateNotes = Boolean(
    unionFittingRateNote ||
      unionInterviewRateNote ||
      nonUnionFittingRateNote ||
      nonUnionInterviewRateNote
  );

  return {
    hasRateNotes,
    hasDescription,
    unionFittingRateNote,
    unionInterviewRateNote,
    nonUnionFittingRateNote,
    nonUnionInterviewRateNote,
  };
};
